
import { createContext, useCallback, useContext, useEffect } from "react";
import { useAuthContext } from "../AuthContext";
import FormHelper from "../../helpers/FormHelper";
import { ServicePriorityEnum, ServiceStatusEnum } from "../../data/Enums";
import useState from "../../hooks/useState";
import IServiceOrder from "../../interfaces/IServiceOrder";
import ServiceOrderService from "../../services/ServiceOrderService";
import Operators from "../../data/Operators";
import DateTimeHelper from "../../helpers/DateTimeHelper";


interface IServiceOrderContext {
    selectedStatus: ServiceStatusEnum[];
    selectedPriority: ServicePriorityEnum[];
    selectedDepartment: number[];
    startDateOperator: string;
    startDate: Date | undefined;
    estimatedCompletionDateOperator: string;
    estimatedCompletionDate: Date | undefined;

    handleChangeSelectedFilters: (
        status: ServiceStatusEnum[],
        priority: ServicePriorityEnum[],
        department: number[],
        startDateOperator: string,
        startDate: Date | undefined,
        estimatedCompletionDateOperator: string,
        estimatedCompletionDate: Date | undefined,
    ) => void;

    filteredServiceOrders: IServiceOrder[];
    handleRefreshServiceOrders: (callback?: (serviceOrders: IServiceOrder[]) => void) => void;
}

interface IStateValues {
    selectedStatus: ServiceStatusEnum[];
    selectedPriority: ServicePriorityEnum[];
    selectedDepartment: number[];
    serviceOrders: IServiceOrder[];
    startDateOperator: string;
    startDate: Date | undefined;
    estimatedCompletionDateOperator: string;
    estimatedCompletionDate: Date | undefined;
}


const ServiceOrderContext = createContext<IServiceOrderContext | undefined>(undefined);

const ServiceOrderContextProvider = (props: React.PropsWithChildren) => {
    const { enterprise } = useAuthContext();
    
    const state = useState<IStateValues>({
        selectedStatus: [
            ServiceStatusEnum.Pending,
            ServiceStatusEnum.Open,
            ServiceStatusEnum.InProgress,
            ServiceStatusEnum.Inspection
        ],
        selectedPriority: [],
        selectedDepartment: [],
        serviceOrders: [],
        startDateOperator: Operators[0],
        startDate: undefined,
        estimatedCompletionDateOperator: Operators[0],
        estimatedCompletionDate: undefined
    });


    useEffect(() => {
        state.reset();
        // eslint-disable-next-line
    }, [enterprise]);


    useEffect(() => {
        if (!enterprise) {
            return;
        }
        
        handleRefreshServiceOrders();
        // eslint-disable-next-line
    }, [state.values.selectedStatus, state.values.selectedPriority, 
        state.values.startDateOperator, state.values.startDate, 
        state.values.estimatedCompletionDateOperator, state.values.estimatedCompletionDate
    ]);


    const handleChangeSelectedFilters = useCallback((
        status: ServiceStatusEnum[],
        priority: ServicePriorityEnum[],
        department: number[],
        startDateOperator: string,
        startDate: Date | undefined,
        estimatedCompletionDateOperator: string,
        estimatedCompletionDate: Date | undefined,
    ) => {
        state.update('selectedStatus', status);
        state.update('selectedPriority', priority);
        state.update('selectedDepartment', department);
        state.update('startDateOperator', startDateOperator);
        state.update('startDate', startDate);
        state.update('estimatedCompletionDateOperator', estimatedCompletionDateOperator);
        state.update('estimatedCompletionDate', estimatedCompletionDate);
        // eslint-disable-next-line
    }, [enterprise]);


    const handleRefreshServiceOrders = useCallback((callback = (serviceOrders: IServiceOrder[]) => {}) => {
        let formData = FormHelper.Create(undefined);
        formData = FormHelper.Add(formData, 'status', state.values.selectedStatus.join(','));
        formData = FormHelper.Add(formData, 'priority', state.values.selectedPriority.join(','));
        formData = FormHelper.Add(formData, 'department', state.values.selectedDepartment.join(','));
        formData = FormHelper.Add(formData, 'startDateOperator', state.values.startDateOperator);
        formData = FormHelper.Add(formData, 'startDate', state.values.startDate ? DateTimeHelper.Format(state.values.startDate) : '');
        formData = FormHelper.Add(formData, 'estimatedCompletionDateOperator', state.values.estimatedCompletionDateOperator);
        formData = FormHelper.Add(formData, 'estimatedCompletionDate', state.values.estimatedCompletionDate ? DateTimeHelper.Format(state.values.estimatedCompletionDate) : '');

        ServiceOrderService.List(formData).then(resp => {
            const serviceOrders = resp.data.serviceOrders as IServiceOrder[];
            state.update('serviceOrders', serviceOrders);
            callback(serviceOrders);
        }).catch(() => {
            state.update('serviceOrders', []);
            callback([]);
        });
        // eslint-disable-next-line
    }, [state.values]);

    
    const data: IServiceOrderContext = {
        selectedStatus: state.values.selectedStatus,
        selectedPriority: state.values.selectedPriority,
        selectedDepartment: state.values.selectedDepartment,

        startDateOperator: state.values.startDateOperator,
        startDate: state.values.startDate,
        
        estimatedCompletionDateOperator: state.values.estimatedCompletionDateOperator,
        estimatedCompletionDate: state.values.estimatedCompletionDate,

        handleChangeSelectedFilters,
        
        filteredServiceOrders: state.values.serviceOrders,
        handleRefreshServiceOrders,
    }

    return (
        <ServiceOrderContext.Provider value={data}>
            {props.children}
        </ServiceOrderContext.Provider>
    );
}


const useServiceOrderContext = () => {
    const context = useContext(ServiceOrderContext);

    if (context === undefined) {
        throw new Error('useServiceOrderContext must be used within a ServiceOrderContextProvider');
    }

    return context;
}

export {
    useServiceOrderContext
}

export default ServiceOrderContextProvider;